import { useEffect, useMemo, useState } from 'react';
import { listAppUsers } from '../data/repo';
import type { AppUserRow } from '../data/types';

/**
 * 사용자 이름 조회 공용 훅.
 *
 * ★ 왜 필요한가 — "등록자·수정자를 이메일이 아니라 이름으로 보여 달라"는
 *   요청이 화면마다 반복돼서, 각자 `listAppUsers()` 를 불러다 로컬
 *   `Record<email,name>` 을 만드는 코드가 여러 화면에 중복돼 있었다
 *   (ReviewPanel.tsx, Activity.tsx). 한 곳으로 모은다.
 *
 * ★ 비활성 사용자도 이름 해석에는 포함한다 — 퇴사·전배로 비활성 처리된
 *   사람이 옛날에 쓴 댓글·버전 이력까지 다시 이메일로 보이면 안 된다.
 *   반면 "멘션 대상 고르기" 처럼 **지금 활성 사용자만** 필요한 화면은
 *   `activeEntries` 를 따로 쓴다.
 */

export interface DirectoryEntry {
  email: string;
  name: string;
}

export interface UserDirectory {
  /** 활성 사용자만 — 멤버 추가·멘션 대상 고르기 등에 쓴다 */
  activeEntries: DirectoryEntry[];
  /** 비활성 포함 전체 — 옛 기록의 이름 해석에 쓴다 */
  all: AppUserRow[];
  loading: boolean;
  /** 이메일 → 표시 이름. 못 찾으면(외부인·삭제된 계정 등) 이메일을 그대로 돌려준다. */
  nameOf: (email: string | null | undefined) => string;
}

export function useUserDirectory(): UserDirectory {
  const [all, setAll] = useState<AppUserRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    listAppUsers()
      .then((rows) => {
        if (alive) setAll(rows);
      })
      .catch(() => {
        /* 이름 표시는 부가 기능 — 실패해도 이메일로 대체되니 조용히 넘어간다 */
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const byEmail = useMemo(() => new Map(all.map((u) => [u.email, u.name || u.email])), [all]);

  const activeEntries = useMemo(
    () => all.filter((u) => u.active).map((u) => ({ email: u.email, name: u.name || u.email })),
    [all]
  );

  function nameOf(email: string | null | undefined): string {
    if (!email) return '—';
    return byEmail.get(email) ?? email;
  }

  return { activeEntries, all, loading, nameOf };
}
